import type { Tone } from "@/components/ui/StatusPill";

/**
 * Una sola maquina de estados, con etiquetas distintas segun el tipo de
 * entrega. Mantener dos maquinas paralelas obligaria a duplicar cada switch
 * del panel para siempre — y la base ya modela una sola.
 */
const ORDER_STATUS: Record<string, { delivery: string; pickup: string; tone: Tone }> = {
  new:        { delivery: "Nuevo",               pickup: "Nuevo",             tone: "info" },
  confirmed:  { delivery: "Confirmado",          pickup: "Confirmado",        tone: "accent" },
  preparing:  { delivery: "Preparando",          pickup: "Preparando",        tone: "accent" },
  ready:      { delivery: "Listo para despacho", pickup: "Listo para recojo", tone: "accent" },
  in_transit: { delivery: "En camino",           pickup: "En camino",         tone: "accent" },
  completed:  { delivery: "Entregado",           pickup: "Recogido",          tone: "ok" },
  cancelled:  { delivery: "Cancelado",           pickup: "Cancelado",         tone: "crit" },
};

export function orderStatus(status: string, fulfillmentType: string) {
  const entry = ORDER_STATUS[status];
  if (!entry) return { label: status, tone: "neutral" as Tone };
  return {
    label: fulfillmentType === "pickup" ? entry.pickup : entry.delivery,
    tone: entry.tone,
  };
}

const PAYMENT_STATUS: Record<string, { label: string; tone: Tone }> = {
  pending:              { label: "Pendiente",           tone: "neutral" },
  pending_verification: { label: "Por verificar",       tone: "warn" },
  paid:                 { label: "Pagado",              tone: "ok" },
  partially_paid:       { label: "Parcial",             tone: "warn" },
  failed:               { label: "Fallido",             tone: "crit" },
  refund_pending:       { label: "Reembolso pendiente", tone: "warn" },
  refunded:             { label: "Reembolsado",         tone: "info" },
  partially_refunded:   { label: "Reembolso parcial",   tone: "info" },
};

export function paymentStatus(status: string) {
  return PAYMENT_STATUS[status] ?? { label: status, tone: "neutral" as Tone };
}

export function fulfillmentLabel(type: string): string {
  return type === "pickup" ? "Recojo" : "Delivery";
}

/**
 * Siguiente paso de la maquina de estados. Un solo boton primario por
 * pantalla: si hay dos, ninguno lo es.
 *
 * Es un espejo de las transiciones que valida transition_order_status(). La
 * base sigue siendo la autoridad: esto solo evita ofrecer un boton que va a
 * ser rechazado.
 */
export function nextStatus(
  status: string,
  fulfillmentType: string,
): { status: string; label: string } | null {
  switch (status) {
    case "new":
      return { status: "confirmed", label: "Confirmar pedido" };
    case "confirmed":
      return { status: "preparing", label: "Empezar a preparar" };
    case "preparing":
      return {
        status: "ready",
        label: fulfillmentType === "pickup" ? "Listo para recojo" : "Listo para despacho",
      };
    case "ready":
      return fulfillmentType === "pickup"
        ? { status: "completed", label: "Marcar como recogido" }
        : { status: "in_transit", label: "Enviar a reparto" };
    case "in_transit":
      return { status: "completed", label: "Marcar como entregado" };
    default:
      return null;
  }
}

export const CANCELLABLE = new Set([
  "new",
  "confirmed",
  "preparing",
  "ready",
  "in_transit",
]);

export const CANCEL_REASONS = [
  "El cliente no contesta",
  "El cliente se arrepintió",
  "Sin stock disponible",
  "Fuera de zona de reparto",
  "Pago no confirmado",
  "Otro motivo",
];

/** Filtros rapidos de la lista. Cada uno responde a una pregunta operativa. */
export const ORDER_FILTERS = [
  { id: "all",       label: "Todos",      statuses: null },
  { id: "new",       label: "Nuevos",     statuses: ["new"] },
  { id: "preparing", label: "Preparando", statuses: ["confirmed", "preparing"] },
  { id: "ready",     label: "Listos",     statuses: ["ready"] },
  { id: "transit",   label: "En camino",  statuses: ["in_transit"] },
  { id: "done",      label: "Entregados", statuses: ["completed"] },
] as const;

export type OrderFilterId = (typeof ORDER_FILTERS)[number]["id"];
